import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, User as UserIcon, ShieldCheck, History, Upload, Trash2, Check, Copy, Download, LogOut, Smartphone, KeyRound, ShieldOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { assetUrl } from "@/context/ErpThemeContext";
import { sec, passwordChecks } from "@/lib/securityApi";
import { useST } from "@/i18n/securityText";
import { PasswordField } from "@/components/profile/PasswordField";
import { useTranslation } from "react-i18next";

const card = "rounded-2xl border border-slate-800 bg-slate-900/40 p-5 md:p-6";
const err = (e) => toast.error(e?.response?.data?.detail?.toString?.() || "Error");
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (e) { return iso || "—"; } };
const initials = (n) => (n || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase();

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-400">{label}</span>
    <div className="mt-1.5">{children}</div>
    {hint && <span className="mt-1 block text-[11px] text-slate-500">{hint}</span>}
  </label>
);

/** Profile, password & 2FA, sign-in activity — the same page for admins, staff (ERP) and customers (customer area). */
export const ProfilePage = ({ area = "portal" }) => {
  const { st } = useST();
  const { loginWithToken, refreshMe } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = ["profile", "security", "activity"].includes(params.get("tab")) ? params.get("tab") : "profile";
  const [p, setP] = useState(null);
  const load = useCallback(() => sec.profile().then(setP).catch(err), []);
  useEffect(() => { load(); }, [load]);
  const newToken = (tok) => { if (tok) loginWithToken(tok); };

  if (!p) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-venka-blue" /></div>;
  const tabs = [["profile", UserIcon, st("tabProfile")], ["security", ShieldCheck, st("tabSecurity")], ["activity", History, st("tabActivity")]];
  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="flex flex-wrap items-center gap-4">
        <Avatar p={p} size="h-16 w-16 text-xl" />
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-100">{st("profile")}</h1>
          <p className="mt-1 text-sm text-slate-400">{st("profileSub")}</p>
        </div>
        <span className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${p.two_factor_enabled ? "bg-venka-green/15 text-venka-green" : "bg-amber-400/15 text-amber-300"}`}>
          <ShieldCheck className="h-3.5 w-3.5" />2FA · {p.two_factor_enabled ? st("tfaOn") : st("tfaOff")}
        </span>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-slate-800">
        {tabs.map(([k, Icon, label]) => (
          <button key={k} type="button" onClick={() => setParams({ tab: k })} data-testid={`profile-tab-${k}`}
            className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-venka-blue text-venka-blue" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>
      {tab === "profile" && <ProfileTab p={p} setP={setP} area={area} onSaved={refreshMe} />}
      {tab === "security" && <SecurityTab p={p} reload={load} newToken={newToken} />}
      {tab === "activity" && <ActivityTab />}
    </div>
  );
};

const Avatar = ({ p, size = "h-20 w-20 text-2xl" }) => (
  p.avatar_url ? <img src={assetUrl(p.avatar_url)} alt="" className={`${size} rounded-full object-cover ring-2 ring-venka-blue/30`} />
    : <span className={`${size} grid place-items-center rounded-full bg-venka-blue/15 font-bold text-venka-blue`}>{initials(p.name)}</span>
);

const ProfileTab = ({ p, setP, area, onSaved }) => {
  const { st } = useST();
  const { i18n } = useTranslation();
  const [f, setF] = useState({ name: p.name || "", phone: p.phone || "", preferred_language: p.preferred_language || "en", notify_email: p.notify_email !== false });
  const [busy, setBusy] = useState(false);
  const dirty = f.name !== (p.name || "") || f.phone !== (p.phone || "") || f.preferred_language !== p.preferred_language || f.notify_email !== (p.notify_email !== false);
  const save = async () => {
    setBusy(true);
    try {
      const np = await sec.update(f); setP(np); toast.success(st("saved")); onSaved?.();
      if (np.preferred_language && np.preferred_language !== (i18n.language || "").slice(0, 2)) i18n.changeLanguage(np.preferred_language);
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const upload = async (file) => { if (!file) return; try { setP(await sec.avatar(file)); onSaved?.(); } catch (e) { err(e); } };
  const removePhoto = async () => { try { setP(await sec.removeAvatar()); onSaved?.(); } catch (e) { err(e); } };
  return (
    <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
      <div className={`${card} flex flex-col items-center text-center`}>
        <Avatar p={p} size="h-28 w-28 text-3xl" />
        <p className="mt-3 font-semibold text-slate-100">{p.name}</p>
        <p className="text-xs text-slate-400">{p.email}</p>
        <div className="mt-4 flex gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-venka-blue">
            <Upload className="h-3.5 w-3.5" />{st("upload")}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="profile-avatar" />
          </label>
          {p.avatar_url && <button type="button" onClick={removePhoto} className="inline-flex items-center gap-1 text-xs text-red-400"><Trash2 className="h-3.5 w-3.5" />{st("remove")}</button>}
        </div>
        <dl className="mt-5 w-full space-y-2 border-t border-slate-800 pt-4 text-left text-xs">
          {area === "portal" && [["role", p.role], ["team", p.team], ["office", p.office]].filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2"><dt className="text-slate-500">{st(k)}</dt><dd className="font-medium capitalize text-slate-200">{v}</dd></div>
          ))}
          <div className="flex justify-between gap-2"><dt className="text-slate-500">{st("member")}</dt><dd className="text-slate-200">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</dd></div>
          <div className="flex justify-between gap-2"><dt className="text-slate-500">{st("lastLogin")}</dt><dd className="text-slate-200">{p.last_login_at ? fmt(p.last_login_at) : "—"}</dd></div>
        </dl>
      </div>
      <div className={`${card} space-y-5`}>
        <div className="grid gap-5 md:grid-cols-2">
          <Field label={st("name")}><Input className="bg-slate-950 border-slate-700" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name" /></Field>
          <Field label={st("email")} hint={st("emailHint")}><Input className="bg-slate-950 border-slate-700 opacity-70" value={p.email} disabled /></Field>
          <Field label={st("phone")}><Input className="bg-slate-950 border-slate-700" value={f.phone} placeholder="+244 …" onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone" /></Field>
          <Field label={st("language")}>
            <select className="h-10 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 text-sm" value={f.preferred_language} onChange={(e) => setF({ ...f, preferred_language: e.target.value })} data-testid="profile-language">
              <option value="pt">Português</option><option value="en">English</option><option value="fr">Français</option>
            </select>
          </Field>
        </div>
        <div>
          <span className="text-xs font-medium text-slate-400">{st("notify")}</span>
          <button type="button" onClick={() => setF({ ...f, notify_email: !f.notify_email })} data-testid="profile-notify"
            className="mt-1.5 flex w-full items-center justify-between gap-4 rounded-xl border border-slate-800 px-4 py-3 text-left">
            <span className="text-sm text-slate-200">{area === "portal" ? st("notifyStaff") : st("notifyCustomer")}</span>
            <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${f.notify_email ? "bg-venka-blue" : "bg-slate-600"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${f.notify_email ? "left-[22px]" : "left-0.5"}`} />
            </span>
          </button>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy || !dirty} className="rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark" data-testid="profile-save">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}{st("save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CodesBox = ({ codes, onDone }) => {
  const { st } = useST();
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");
  const copy = () => { try { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { /* ignore */ } };
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([`VENKA World Express — backup codes\n${new Date().toLocaleString()}\n\n${text}\n\nEach code works once.\n`], { type: "text/plain" }));
    a.download = "venka-backup-codes.txt"; a.click();
  };
  return (
    <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-4" data-testid="backup-codes">
      <p className="text-sm font-semibold text-amber-200">{st("backup")}</p>
      <p className="mt-1 text-xs text-slate-400">{st("backupSub")}</p>
      <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm sm:grid-cols-5">
        {codes.map((c) => <span key={c} className="rounded-md bg-slate-950 px-2 py-1.5 text-center text-slate-100">{c}</span>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-lg border-slate-700" onClick={copy}><Copy className="mr-1.5 h-3.5 w-3.5" />{copied ? st("copied") : st("copy")}</Button>
        <Button size="sm" variant="outline" className="rounded-lg border-slate-700" onClick={download}><Download className="mr-1.5 h-3.5 w-3.5" />{st("download")}</Button>
        <Button size="sm" className="ml-auto rounded-lg bg-venka-blue text-white" onClick={onDone} data-testid="backup-done"><Check className="mr-1.5 h-3.5 w-3.5" />{st("done")}</Button>
      </div>
    </div>
  );
};

const SecurityTab = ({ p, reload, newToken }) => {
  const { st } = useST();
  // password
  const [cur, setCur] = useState(""); const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const changePw = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error(st("mismatch")); return; }
    setBusy(true);
    try { const r = await sec.changePassword(cur, pw); newToken(r.access_token); toast.success(st("changed")); setCur(""); setPw(""); setPw2(""); reload(); } catch (x) { err(x); } finally { setBusy(false); }
  };
  // 2FA
  const [setup, setSetup] = useState(null); const [code, setCode] = useState(""); const [codes, setCodes] = useState(null);
  const [mode, setMode] = useState(null); const [dPw, setDPw] = useState(""); const [dCode, setDCode] = useState("");
  const start = async () => { try { setSetup(await sec.setup2fa()); setCode(""); } catch (x) { err(x); } };
  const enable = async () => { try { const r = await sec.enable2fa(code); newToken(r.access_token); setCodes(r.backup_codes); setSetup(null); reload(); } catch (x) { err(x); } };
  const disable = async () => { try { await sec.disable2fa(dPw, dCode); setMode(null); setDPw(""); setDCode(""); reload(); } catch (x) { err(x); } };
  const regen = async () => { try { setCodes((await sec.newBackupCodes(dCode)).backup_codes); setMode(null); setDCode(""); reload(); } catch (x) { err(x); } };
  const revoke = async () => { try { const r = await sec.revokeSessions(); newToken(r.access_token); toast.success(st("signedOut")); } catch (x) { err(x); } };

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <form onSubmit={changePw} className={`${card} space-y-4`} data-testid="password-form">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-venka-blue/15 text-venka-blue"><KeyRound className="h-5 w-5" /></span>
          <div>
            <h2 className="font-semibold text-slate-100">{st("password")}</h2>
            <p className="text-xs text-slate-400">{st("passwordSub")}</p>
            {p.password_changed_at && <p className="mt-1 text-[11px] text-slate-500">{st("lastChanged")}: {fmt(p.password_changed_at)}</p>}
          </div>
        </div>
        <PasswordField label={st("current")} value={cur} onChange={setCur} autoComplete="current-password" testid="pw-current" />
        <PasswordField label={st("newPw")} value={pw} onChange={setPw} meter email={p.email} testid="pw-new" />
        <PasswordField label={st("confirm")} value={pw2} onChange={setPw2} testid="pw-confirm" />
        {pw2 && pw !== pw2 && <p className="text-xs text-red-400">{st("mismatch")}</p>}
        <Button type="submit" disabled={busy || !cur || !passwordChecks(pw, p.email).ok || pw !== pw2} className="rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark" data-testid="pw-submit">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{st("change")}
        </Button>
      </form>

      <div className={`${card} space-y-4`} data-testid="tfa-card">
        <div className="flex items-start gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${p.two_factor_enabled ? "bg-venka-green/15 text-venka-green" : "bg-amber-400/15 text-amber-300"}`}><Smartphone className="h-5 w-5" /></span>
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 font-semibold text-slate-100">{st("tfa")}
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.two_factor_enabled ? "bg-venka-green/15 text-venka-green" : "bg-slate-700 text-slate-300"}`}>{p.two_factor_enabled ? st("tfaOn") : st("tfaOff")}</span>
            </h2>
            <p className="text-xs text-slate-400">{st("tfaSub")}</p>
          </div>
        </div>

        {codes && <CodesBox codes={codes} onDone={() => setCodes(null)} />}

        {!p.two_factor_enabled && !setup && !codes && (
          <Button onClick={start} className="rounded-xl bg-venka-green text-white hover:bg-venka-green/90" data-testid="tfa-start"><ShieldCheck className="mr-2 h-4 w-4" />{st("tfaStart")}</Button>
        )}
        {setup && (
          <div className="space-y-4 rounded-xl border border-slate-800 p-4">
            <p className="text-sm font-medium text-slate-200">{st("tfaStep1")}</p>
            <div className="flex flex-wrap items-center gap-4">
              <img src={setup.qr} alt="QR" className="h-44 w-44 rounded-xl bg-white p-2" data-testid="tfa-qr" />
              <div className="min-w-0 text-xs text-slate-400">
                {st("tfaManual")}
                <p className="mt-1 break-all rounded-md bg-slate-950 px-2 py-1.5 font-mono text-sm text-slate-100" data-testid="tfa-secret">{setup.secret}</p>
              </div>
            </div>
            <p className="text-sm font-medium text-slate-200">{st("tfaStep2")}</p>
            <div className="flex flex-wrap gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="123456" autoComplete="one-time-code"
                className="h-11 w-40 bg-slate-950 border-slate-700 text-center font-mono text-lg tracking-[0.3em]" data-testid="tfa-code" />
              <Button onClick={enable} disabled={code.length !== 6} className="h-11 rounded-xl bg-venka-blue text-white" data-testid="tfa-verify">{st("tfaVerify")}</Button>
              <Button variant="ghost" onClick={() => setSetup(null)} className="h-11 text-slate-400">{st("cancel")}</Button>
            </div>
          </div>
        )}
        {p.two_factor_enabled && !codes && (
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-sm text-venka-green"><Check className="h-4 w-4" />{st("tfaEnabled")}{p.two_factor_enabled_at ? ` ${st("since")} ${new Date(p.two_factor_enabled_at).toLocaleDateString()}` : ""}</p>
            <p className="text-xs text-slate-400">{st("backupLeft", { n: p.backup_codes_left })}</p>
            {!mode && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-lg border-slate-700" onClick={() => setMode("codes")} data-testid="tfa-newcodes"><RefreshCw className="mr-1.5 h-3.5 w-3.5" />{st("newCodes")}</Button>
                <Button size="sm" variant="outline" className="rounded-lg border-red-500/40 text-red-400" onClick={() => setMode("disable")} data-testid="tfa-disable"><ShieldOff className="mr-1.5 h-3.5 w-3.5" />{st("tfaDisable")}</Button>
              </div>
            )}
            {mode && (
              <div className="space-y-3 rounded-xl border border-slate-800 p-4">
                {mode === "disable" && <p className="text-xs text-slate-400">{st("disableConfirm")}</p>}
                {mode === "disable" && <PasswordField label={st("current")} value={dPw} onChange={setDPw} autoComplete="current-password" />}
                <Field label={st("codeLabel")}><Input value={dCode} onChange={(e) => setDCode(e.target.value)} className="bg-slate-950 border-slate-700 font-mono" data-testid="tfa-confirm-code" /></Field>
                <div className="flex gap-2">
                  <Button size="sm" onClick={mode === "disable" ? disable : regen} disabled={!dCode || (mode === "disable" && !dPw)}
                    className={`rounded-lg ${mode === "disable" ? "bg-red-600 text-white" : "bg-venka-blue text-white"}`} data-testid="tfa-confirm">
                    {mode === "disable" ? st("tfaDisable") : st("generate")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setMode(null); setDCode(""); setDPw(""); }}>{st("cancel")}</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={`${card} xl:col-span-2 flex flex-wrap items-center justify-between gap-4`}>
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-800 text-slate-300"><LogOut className="h-5 w-5" /></span>
          <div><h2 className="font-semibold text-slate-100">{st("sessions")}</h2><p className="text-xs text-slate-400">{st("sessionsSub")}</p></div>
        </div>
        <Button variant="outline" className="rounded-xl border-slate-700" onClick={revoke} data-testid="revoke-sessions"><LogOut className="mr-2 h-4 w-4" />{st("signOutOthers")}</Button>
      </div>
    </div>
  );
};

const ActivityTab = () => {
  const { st } = useST();
  const [items, setItems] = useState(null);
  useEffect(() => { sec.activity().then(setItems).catch(() => setItems([])); }, []);
  if (!items) return <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-venka-blue" /></div>;
  return (
    <div className={`${card} overflow-x-auto`} data-testid="activity">
      {items.length === 0 ? <p className="text-sm text-slate-400">{st("noActivity")}</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="px-3 py-2">{st("when")}</th><th className="px-3 py-2">{st("device")}</th><th className="px-3 py-2">{st("ip")}</th><th className="px-3 py-2">{st("method")}</th>
          </tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-slate-800">
                <td className="px-3 py-2 whitespace-nowrap text-slate-300">{fmt(a.at)}</td>
                <td className="px-3 py-2 text-slate-200">{a.device}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-400">{a.ip}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.method === "password+2fa" ? "bg-venka-green/15 text-venka-green" : "bg-slate-800 text-slate-300"}`}>{st(`m_${a.method}`)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ProfilePage;
